import { joinUrl } from "lib/utils/connection";
import { isHttpSecure } from "lib/utils/utils";
import { gatewayServiceBaseUrl } from "lib/consts/arcGateway";
import { authedFetch } from "lib/utils/authedFetch";
import { messageFromFailedResponse } from "lib/utils/userFacingApiError";

const DEFAULT_ROBOT_PROFILE_PORT = "12409";

export type RobotProfileId = "go2" | "go2w" | "gb" | "gbw" | "lynx" | "tron1" | "spot";

export type RobotConnectionConfig = {
    method: string;
    params: Record<string, string>;
};

export type RobotProfileConfig = {
    active?: RobotProfileId | null;
    connection?: RobotConnectionConfig | null;
    running?: boolean;
};

export type RobotProfileStatus = {
    connected?: boolean;
    state?: string;
    last_error?: string | null;
    updated_at?: number;
    active_service?: string | null;
    active_profile?: string | null;
    child_pid?: number | null;
    bridge_log?: string | null;
};

export type RobotEthernetStatus = {
    interface?: string | null;
    state?: string | null;
    connection?: string | null;
    autoconnect?: boolean | null;
    ipv4?: string | null;
    ipv4_gateway?: string | null;
    mac?: string | null;
    error?: string | null;
} | null;

export type RobotProfileResponse = {
    ok?: boolean;
    profile?: RobotProfileConfig;
    status?: RobotProfileStatus;
    ethernet?: RobotEthernetStatus;
};

export type RobotProfileCatalogEntry = {
    service?: string;
    methods: string[];
    default_method?: string;
    default_params?: Record<string, string>;
    method_params?: Record<string, Record<string, string>>;
};

export type RobotProfileCatalog = {
    profiles?: Record<string, RobotProfileCatalogEntry>;
};

function parseApiResponse<T>(res: Response, text: string): T {
    let data: unknown = {};
    try {
        data = text ? JSON.parse(text) : {};
    } catch {
        data = { raw: text };
    }
    if (!res.ok) {
        const detail =
            typeof data === "object" && data !== null && "detail" in data
                ? String((data as { detail: unknown }).detail)
                : text || res.statusText;
        throw new Error(messageFromFailedResponse(res.status, detail, "Couldn't complete the robot profile request. Try again."));
    }
    return data as T;
}

export function getRobotProfileBaseUrl(
    selectedArcHost?: string | null,
    isSecure: boolean = isHttpSecure(),
    arcId?: string | null
): string | null {
    const explicit = process.env.REACT_APP_ROBOT_PROFILE_BASE_URL?.trim();
    if (explicit) return explicit.replace(/\/$/, "");

    const port = process.env.REACT_APP_ROBOT_PROFILE_PORT?.trim() || DEFAULT_ROBOT_PROFILE_PORT;
    const proxyHost =
        selectedArcHost?.trim() ||
        process.env.REACT_APP_ROBOT_PROFILE_HOST?.trim();
    const host =
        proxyHost ||
        process.env.REACT_APP_SINGLE_ARC_HOSTNAME?.trim() ||
        process.env.REACT_APP_HOST_OVERRIDE?.trim() ||
        (typeof window !== "undefined" ? window.location.hostname : "");
    if (!host) return null;

    const protocol = isSecure ? "https" : "http";
    // Direct ARC mode uses arc_gateway; fleet mode uses the proxy's path-routed
    // origin. A fleet ARC without a proxy host is unavailable.
    //
    // NO HOSTNAME CHECK HERE. This used to skip the gateway when the page was
    // reached by IP literal, back when arc_gateway answered only on
    // offline.r2c2.ai. It now binds :80 with no host matcher, so an IP-LAN
    // session is served by the same gateway — sending it to :12409 instead
    // would bypass a proxy sitting right there.
    const gatewayBase = gatewayServiceBaseUrl("robotProfile", arcId, proxyHost);
    if (gatewayBase) return gatewayBase;
    if (arcId) return null;

    return `${protocol}://${host}:${port}`;
}

export async function getRobotProfiles(baseUrl: string): Promise<RobotProfileCatalog> {
    const res = await authedFetch(joinUrl(baseUrl, "/profiles"));
    return parseApiResponse<RobotProfileCatalog>(res, await res.text());
}

export async function getRobotProfile(baseUrl: string): Promise<RobotProfileResponse> {
    const res = await authedFetch(joinUrl(baseUrl, "/profile"));
    return parseApiResponse<RobotProfileResponse>(res, await res.text());
}

/**
 * Detect whether the robot-profile service is present without requiring a
 * healthy profile. Any response other than 404 means the service route exists.
 */
export async function probeRobotProfileService(baseUrl: string | null): Promise<boolean> {
    if (!baseUrl) return false;
    try {
        const res = await authedFetch(joinUrl(baseUrl, "/profile"), { cache: "no-store" });
        return res.status !== 404;
    } catch {
        return false;
    }
}

/** Persist profile + bridge YAML only (no ethernet / spawn). */
export async function saveRobotProfile(
    baseUrl: string,
    profile: RobotProfileId,
    connection: RobotConnectionConfig,
    signal?: AbortSignal
): Promise<RobotProfileResponse> {
    const res = await authedFetch(joinUrl(baseUrl, "/profile/save"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ profile, connection }),
        signal,
    });
    return parseApiResponse<RobotProfileResponse>(res, await res.text());
}

/** Apply ethernet (if needed) and spawn the bridge child. */
export async function connectRobotProfile(
    baseUrl: string,
    profile: RobotProfileId,
    connection: RobotConnectionConfig,
    signal?: AbortSignal
): Promise<RobotProfileResponse> {
    const res = await authedFetch(joinUrl(baseUrl, "/profile/connect"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ profile, connection }),
        signal,
    });
    // 499 = cancelled mid-connect
    if (res.status === 499) {
        throw new Error("cancelled");
    }
    return parseApiResponse<RobotProfileResponse>(res, await res.text());
}

/** @deprecated Prefer saveRobotProfile / connectRobotProfile */
export async function postRobotProfile(
    baseUrl: string,
    profile: RobotProfileId,
    connection: RobotConnectionConfig
): Promise<RobotProfileResponse> {
    return saveRobotProfile(baseUrl, profile, connection);
}

export async function deleteRobotProfile(
    baseUrl: string,
    signal?: AbortSignal
): Promise<RobotProfileResponse> {
    let res = await authedFetch(joinUrl(baseUrl, "/profile"), { method: "DELETE", signal });
    if (res.status === 405) {
        res = await authedFetch(joinUrl(baseUrl, "/profile/disconnect"), {
            method: "POST",
            signal,
        });
    }
    return parseApiResponse<RobotProfileResponse>(res, await res.text());
}
